import {
  type ContractDetail,
  type FileInfo,
  PAYMENT_KINDS,
  PAYMENT_REQUEST_STATUSES,
  seesMoney,
} from '@epc/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { Paperclip } from 'lucide-react';
import { useState } from 'react';
import { FileLink } from '../../components/FileLink';
import { api } from '../../lib/api';
import { tenge } from '../../lib/format';
import { useMe } from '../../lib/session';
import { Button } from '../../ui/button';
import { ErrorBox, Spinner, toast } from '../../ui/feedback';
import { Checkbox, Field, Input } from '../../ui/form';
import { Panel } from '../../ui/layout';
import { paymentRequestsQuery } from '../procurement/payments';

/**
 * ДГ-12: документы по договору в одном месте — файлы самого договора и документы всех его заявок на
 * оплату по комплектам (накладные, ЭСФ, КС-2 и КС-3, акты сверки).
 */
export function ContractDocsPanel({ d, canUpload }: { d: ContractDetail; canUpload: boolean }) {
  const qc = useQueryClient();
  const me = useMe();
  // Скан договора и документы оплаты — с уровнем денег «Акты и счета» (ДС-04)
  const money = seesMoney(me.access, 'acts_invoices');
  const filesKey = ['files', 'contract', d.id];
  const files = useQuery({
    queryKey: filesKey,
    queryFn: () => api<FileInfo[]>('/files', { query: { entityType: 'contract', entityId: d.id } }),
    enabled: money,
  });
  const prs = useQuery({ ...paymentRequestsQuery({ contractId: d.id }), enabled: d.direction === 'expense' });
  const upload = useMutation({
    mutationFn: (file: File) => {
      const form = new FormData();
      form.append('entityType', 'contract');
      form.append('entityId', d.id);
      form.append('file', file);
      return api<FileInfo>('/files', { form, method: 'POST' });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: filesKey });
      toast('Файл приложен к договору');
    },
  });
  const list = (prs.data ?? []).filter((p) => p.status !== 'cancelled');
  return (
    <Panel
      title="Документы по договору"
      meta="файлы договора и документы заявок на оплату (ДГ-12)"
      actions={
        canUpload && money ? (
          <label className="inline-flex min-h-11 cursor-pointer items-center gap-1.5 rounded-md border border-line px-3 text-[13px] font-medium text-ink hover:bg-tile md:min-h-9">
            <input
              type="file"
              className="sr-only"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) upload.mutate(f);
                e.target.value = '';
              }}
            />
            <Paperclip className="size-4" aria-hidden />
            Приложить файл
          </label>
        ) : null
      }
    >
      <div className="flex flex-col gap-4 text-[14px]">
        <section>
          <h3 className="text-[13px] font-semibold text-ink">Договор</h3>
          {!money ? (
            <p className="mt-1 text-ink-2">
              Скан договора и файлы документов оплаты видны с уровнем денег «Акты и счета».
            </p>
          ) : files.isPending ? (
            <Spinner />
          ) : files.data?.length ? (
            <ul className="mt-1 flex flex-col gap-0.5">
              {files.data.map((f) => (
                <li key={f.id}>
                  <FileLink file={f} />
                </li>
              ))}
            </ul>
          ) : (
            <p className="mt-1 text-ink-2">Файлов нет: приложите скан подписанного договора.</p>
          )}
        </section>
        {d.direction === 'expense' ? (
          prs.isPending ? (
            <Spinner />
          ) : list.length ? (
            list.map((p) => (
              <section key={p.id}>
                <h3 className="text-[13px] font-semibold text-ink">
                  {p.number} · {PAYMENT_KINDS[p.kind].toLowerCase()} ·{' '}
                  {PAYMENT_REQUEST_STATUSES[p.status].toLowerCase()}
                  <span
                    className={clsx('ml-2 font-normal', p.docsComplete ? 'text-success' : 'text-warning')}
                  >
                    {p.docsComplete ? 'комплект полный' : 'комплект неполный'}
                  </span>
                </h3>
                <ul className="mt-1 flex flex-col gap-1">
                  {p.docs.map((doc) => (
                    <li key={doc.key} className="text-[13px]">
                      <span className={doc.ok ? 'text-ink' : 'text-ink-2'}>
                        {doc.ok ? '✓' : '—'} {doc.title}
                      </span>
                      {doc.files.length ? (
                        <span className="ml-1">
                          {doc.files.map((f, i) => (
                            <span key={f.id}>
                              {i ? ', ' : ': '}
                              <FileLink file={f} />
                            </span>
                          ))}
                        </span>
                      ) : null}
                    </li>
                  ))}
                </ul>
              </section>
            ))
          ) : (
            <p className="text-ink-2">Заявок на оплату по договору пока нет.</p>
          )
        ) : null}
        <ErrorBox error={upload.error ?? (money ? files.error : null) ?? prs.error} />
      </div>
    </Panel>
  );
}

/** Строка «поставка»: позиция спецификации и состояние — поставлено, просрочена или ждём (ДГ-09, ДГ-13) */
export function DeliveryState({
  r,
  d,
  today,
}: {
  r: ContractDetail['schedule'][number];
  d: ContractDetail;
  today: string;
}) {
  if (r.kind !== 'delivery') return null;
  const item = r.contractItemId ? d.items.find((i) => i.id === r.contractItemId) : undefined;
  const late = !r.done && r.plannedDate !== null && r.plannedDate !== undefined && r.plannedDate < today;
  return (
    <span className="block text-[12px] text-ink-2">
      {item ? `${item.name}, ${item.unit} · ` : ''}
      {r.done ? (
        'поставлено'
      ) : late ? (
        <span className="text-danger">поставка просрочена</span>
      ) : (
        'ждём поставку'
      )}
    </span>
  );
}

/** Сколько по плановому платежу уже в заявках и оплачено (ФН-02) */
export function ScheduleCoverage({ r }: { r: ContractDetail['schedule'][number] }) {
  const me = useMe();
  if (r.kind !== 'payment' || !seesMoney(me.access, 'acts_invoices')) return null;
  if (!r.requested && !r.paid)
    return <span className="block text-[12px] text-ink-2">заявок на оплату нет</span>;
  return (
    <span className="block text-[12px] text-ink-2">
      в заявках {tenge(r.requested)}, оплачено {tenge(r.paid)}
    </span>
  );
}

/**
 * Договор требует гарантию возврата аванса: она войдёт в комплект документов к авансу (ФН-19). В проекте
 * договора отметку ставит тот, кто его готовит; после отправки на согласование — финансовый менеджер
 * юрлица или компании, с причиной.
 */
export function AdvanceGuaranteeToggle({
  d,
  canDraft,
  canDecide,
  onSaved,
}: {
  d: ContractDetail;
  /** Готовит проект договора */
  canDraft: boolean;
  /** Финансовый менеджер юрлица или компании */
  canDecide: boolean;
  onSaved: (x: ContractDetail) => void;
}) {
  const [want, setWant] = useState<boolean | null>(null);
  const [reason, setReason] = useState('');
  const save = useMutation({
    mutationFn: (v: { required: boolean; reason?: string }) =>
      api<ContractDetail>(`/contracts/${d.id}/advance-guarantee`, { method: 'PUT', body: v }),
    onSuccess: (x) => {
      onSaved(x);
      setWant(null);
      setReason('');
      toast('Сохранено');
    },
    onError: (e) => toast((e as Error).message, 'danger'),
  });
  if (d.direction !== 'expense') return null;
  const draft = d.status === 'draft';
  const note = d.advanceGuaranteeNote ? (
    <span className="block text-[12px] text-ink-2">
      Изменено после отправки на согласование: {d.advanceGuaranteeNote}
    </span>
  ) : null;
  if (draft && (canDraft || canDecide))
    return (
      <Checkbox
        label="Договор требует гарантию возврата аванса"
        description="Гарантия войдёт в комплект документов к оплате аванса"
        checked={d.advanceGuarantee}
        disabled={save.isPending}
        onChange={(e) => save.mutate({ required: e.target.checked })}
      />
    );
  const state = (
    <p className="text-[13px] text-ink-2">
      Гарантия возврата аванса: {d.advanceGuarantee ? 'требуется по договору' : 'не требуется'}
      {note}
    </p>
  );
  if (!canDecide || d.status === 'terminated') return state;
  return (
    <div className="flex flex-col gap-2">
      <Checkbox
        label="Договор требует гарантию возврата аванса"
        description="После отправки на согласование отметку меняет финансовый менеджер с причиной; правка видна в истории договора"
        checked={want ?? d.advanceGuarantee}
        disabled={save.isPending}
        onChange={(e) => setWant(e.target.checked)}
      />
      {note}
      {want !== null && want !== d.advanceGuarantee ? (
        <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
          <Field label="Почему меняется требование гарантии" className="flex-1">
            <Input value={reason} onChange={(e) => setReason(e.target.value)} />
          </Field>
          <Button
            variant="primary"
            loading={save.isPending}
            disabled={reason.trim().length < 3}
            onClick={() => save.mutate({ required: want, reason: reason.trim() })}
          >
            Сохранить
          </Button>
        </div>
      ) : null}
    </div>
  );
}
